export type RankDevice = 'android' | 'desktop';
export type RankEngine = 'bing' | 'google';

export type SerpCard = {
  pos: number;
  title: string;
  url: string;
  host: string;
  snippet?: string;
};

export type RankSerpSnapshot = {
  date: string;
  query: string;
  items: SerpCard[];
  html?: string;
};

export type RankGeoRow = {
  id: string;
  country: string;
  city?: string;
  locale?: string;
  device: RankDevice;
  label: string;
  days: Record<string, number | null>;
  samples: Record<string, number>;
  hours?: Record<string, number | null>;
  hourSamples?: Record<string, number>;
  serp?: RankSerpSnapshot;
};

export type RankKeywordRow = {
  taskId: string;
  site: string;
  keyword: string;
  engine: RankEngine;
  searches: number;
  clicks: number;
  best: Record<string, number | null>;
  bestHours?: Record<string, number | null>;
  geos: RankGeoRow[];
};

export type RankProbeInfo = {
  lastAt?: string;
  nextAt?: string;
  requestedAt?: string;
};

export type RankMatrix = {
  days: string[];
  hours?: string[];
  keywords: RankKeywordRow[];
  probe?: RankProbeInfo;
};

export function formatRank(value: number | null | undefined): string {
  if (value === undefined) return '—';
  if (value === null) return '100+';
  return String(value);
}

export function rankTone(value: number | null | undefined): 'top' | 'mid' | 'low' | 'miss' | 'empty' {
  if (value === undefined) return 'empty';
  if (value === null) return 'miss';
  if (value <= 3) return 'top';
  if (value <= 10) return 'mid';
  return 'low';
}

export function latestGeoPosition(geo: RankGeoRow, days: string[]): number | null | undefined {
  for (let i = days.length - 1; i >= 0; i -= 1) {
    const date = days[i]!;
    if (date in geo.days) return geo.days[date];
  }
  return undefined;
}

export function snapshotRank(geo: RankGeoRow, site: string): number | null | undefined {
  const items = geo.serp?.items;
  if (!items?.length) return undefined;
  const hit = items.find((card) => hostMatchesSite(card.host, site));
  return hit ? hit.pos : null;
}

export function currentGeoPosition(geo: RankGeoRow, days: string[], site: string): number | null | undefined {
  const fromSerp = snapshotRank(geo, site);
  if (fromSerp !== undefined) return fromSerp;
  const hours = geo.hours ? Object.keys(geo.hours).sort() : [];
  if (hours.length) {
    for (let i = hours.length - 1; i >= 0; i -= 1) {
      const key = hours[i]!;
      if (key in geo.hours!) return geo.hours![key];
    }
  }
  return latestGeoPosition(geo, days);
}

export function geoValues(geo: RankGeoRow, grain: 'day' | 'hour'): Record<string, number | null> {
  return grain === 'hour' ? (geo.hours ?? {}) : geo.days;
}

export function keywordValues(row: RankKeywordRow, grain: 'day' | 'hour'): Record<string, number | null> {
  return grain === 'hour' ? (row.bestHours ?? {}) : row.best;
}

export function dayLabel(date: string): string {
  const [, month, day] = date.split('-');
  return `${Number(day)}.${Number(month)}`;
}

export function hourLabel(hour: string): string {
  const stamp = Date.parse(`${hour}:00:00.000Z`);
  if (!Number.isFinite(stamp)) return hour.slice(-2);
  const d = new Date(stamp);
  return `${String(d.getHours()).padStart(2, '0')}:00`;
}

export function periodTitle(key: string, grain: 'day' | 'hour'): string {
  if (grain === 'hour') {
    const stamp = Date.parse(`${key}:00:00.000Z`);
    if (!Number.isFinite(stamp)) return key;
    return new Intl.DateTimeFormat('ru', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' }).format(new Date(stamp));
  }
  const stamp = Date.parse(`${key}T12:00:00.000Z`);
  if (!Number.isFinite(stamp)) return dayLabel(key);
  return new Intl.DateTimeFormat('ru', { day: 'numeric', month: 'long' }).format(new Date(stamp));
}

export function probeLabel(probe?: RankProbeInfo): string {
  if (probe?.lastAt) {
    const at = new Date(probe.lastAt);
    if (Number.isFinite(at.getTime())) {
      const time = new Intl.DateTimeFormat('ru', { hour: '2-digit', minute: '2-digit' }).format(at);
      return `последняя проверка ${time} · каждый час`;
    }
  }
  return 'проверка каждый час уникальным браузером';
}

export function trimEmptyHours(hours: string[], keywords: RankKeywordRow[]): string[] {
  let first = -1;
  let last = -1;
  for (let i = 0; i < hours.length; i += 1) {
    const key = hours[i]!;
    const used = keywords.some((row) => key in (row.bestHours ?? {}) || row.geos.some((geo) => geo.hours && key in geo.hours));
    if (!used) continue;
    if (first < 0) first = i;
    last = i;
  }
  if (first < 0) return hours.slice(-12);
  const from = Math.max(0, first);
  return hours.slice(from, last + 1);
}

export function hostMatchesSite(host: string, site: string): boolean {
  const h = host.toLowerCase().replace(/^www\./, '');
  const s = site.toLowerCase().replace(/^www\./, '').replace(/^https?:\/\//, '').split('/')[0] ?? '';
  return Boolean(h && s && (h === s || h.endsWith(`.${s}`)));
}

const KEY_COUNTRIES = new Set([
  'AE', 'AR', 'AT', 'AU', 'BE', 'BR', 'CA', 'CH', 'CL', 'CO', 'CZ', 'DE', 'ES', 'FR',
  'GB', 'ID', 'IE', 'IL', 'IN', 'IT', 'JP', 'KR', 'KZ', 'MX', 'MY', 'NL', 'NZ', 'PL',
  'PT', 'RO', 'RU', 'SA', 'SE', 'SG', 'TH', 'TR', 'UA', 'US', 'UZ', 'VN', 'ZA',
]);

const VISIBLE_GEOS = 8;

export function countryName(code: string): string {
  const cc = code.trim().toUpperCase();
  if (!cc || cc === 'XX') return 'все гео';
  try {
    return new Intl.DisplayNames(['ru'], { type: 'region', style: 'short' }).of(cc) || cc;
  } catch {
    return cc;
  }
}

export function shortGeoLabel(geo: Pick<RankGeoRow, 'country' | 'device'>): string {
  const form = geo.device === 'desktop' ? 'ПК' : 'Android';
  return `${countryName(geo.country)} · ${form}`;
}

export function isKeyCountry(code?: string): boolean {
  return KEY_COUNTRIES.has(String(code || '').trim().toUpperCase());
}

export function geoSampleCount(geo: RankGeoRow): number {
  return Object.values(geo.samples).reduce((sum, n) => sum + (Number(n) || 0), 0);
}

export function compareGeos(a: RankGeoRow, b: RankGeoRow, days: string[]): number {
  const ra = latestGeoPosition(a, days);
  const rb = latestGeoPosition(b, days);
  const ka = rankSortKey(ra);
  const kb = rankSortKey(rb);
  if (ka !== kb) return ka - kb;
  if (typeof ra === 'number' && typeof rb === 'number' && ra !== rb) return ra - rb;
  const keyA = isKeyCountry(a.country) ? 0 : 1;
  const keyB = isKeyCountry(b.country) ? 0 : 1;
  if (keyA !== keyB) return keyA - keyB;
  const samples = geoSampleCount(b) - geoSampleCount(a);
  if (samples) return samples;
  return a.country.localeCompare(b.country) || a.device.localeCompare(b.device);
}

export function pickVisibleGeos(
  geos: RankGeoRow[],
  days: string[],
  showAll = false,
  limit = VISIBLE_GEOS,
): { shown: RankGeoRow[]; hidden: number } {
  const ordered = [...geos].sort((a, b) => compareGeos(a, b, days));
  if (showAll || ordered.length <= limit) return { shown: ordered, hidden: 0 };
  const preferred = ordered.filter((geo) => isPreferredGeo(geo, days));
  const shown = (preferred.length ? preferred : ordered).slice(0, limit);
  return { shown, hidden: ordered.length - shown.length };
}

export function trimEmptyDays(days: string[], keywords: RankKeywordRow[]): string[] {
  let first = -1;
  let last = -1;
  for (let i = 0; i < days.length; i += 1) {
    const date = days[i]!;
    const used = keywords.some((row) => date in row.best || row.geos.some((geo) => date in geo.days));
    if (!used) continue;
    if (first < 0) first = i;
    last = i;
  }
  if (first < 0) return days.slice(-7);
  return days.slice(first, last + 1);
}

function isPreferredGeo(geo: RankGeoRow, days: string[]): boolean {
  if (isKeyCountry(geo.country)) return true;
  return typeof latestGeoPosition(geo, days) === 'number';
}

function rankSortKey(value: number | null | undefined): number {
  if (typeof value === 'number') return 0;
  if (value === null) return 1;
  return 2;
}
